// Iter 548 — Panel Antrean Shopee Press (Haikal / tim Mesin Press).
//
// Menyambungkan antrean Press dari `GET /api/shopee/press/queue` dgn aksi
// `POST /api/shopee/{job_id}/press/start` & `/press/done`.
//
// Backend gate memastikan tombol Mulai Press hanya dapat dieksekusi saat
// press_state=ready (artinya warehouse_state=completed + sablon_state=done
// + bordir_state=done utk kombinasi). Frontend disable tombol sesuai state.
import { ShopeeCancelTag } from "./shopee_cancel_tag";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChannelTag, TokopediaAttachments } from "./channel";
import { LateBanner } from "./press_summary";

const WHO = { rido: "barang dari Rido (Gudang)", ibnu: "sablon/DTF dari Ibnu", bordir: "Bordir selesai" };
export const shopeeWaitText = (job) => {
  const p = (job.press_waiting_parts || []).filter(x => WHO[x]);
  return p.length ? `Belum bisa dikerjakan — menunggu ${p.map(x => WHO[x]).join(" dan ")}` : `Belum bisa dikerjakan — ${job.press_waiting_reason || "belum memenuhi syarat untuk Press"}`;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function _fmtDate(iso) {
  if (!iso || typeof iso !== "string") return "";
  const s = iso.length >= 10 ? iso.slice(0, 10) : iso;
  const p = s.split("-");
  if (p.length !== 3) return s;
  return `${p[2]}/${p[1]}/${p[0].slice(2)}`;
}

function processLabel(proc) {
  if (proc === "bordir_sablon_dtf") return "Bordir + Sablon DTF";
  if (proc === "sablon_dtf") return "Sablon DTF";
  return proc || "";
}

// Iter 553 — Ambil file_id mockup FIX terbaru untuk thumbnail (konsisten
//   dengan panel Sablon). Prioritas: snapshot Gudang (final FIX) → mockup
//   aktif terbaru. Return "" bila tidak ada file valid (UI tampilkan
//   placeholder "Mockup belum tersedia", bukan broken image).
function pickMockupFileId(job) {
  // Iter 651 — Pilih file GAMBAR dari snapshot (file pertama bisa PDF/non-gambar → thumbnail kosong).
  const img = (job?.warehouse_sent_snapshot || []).find(x => x?.file_id && (!x.content_type || String(x.content_type).startsWith("image")));
  if (img) return img.file_id;
  const snap = (job?.warehouse_sent_snapshot || []).length ? "" : (job?.warehouse_sent_snapshot_file_ids || [])[0];
  if (snap) return snap;
  const mockups = (job?.mockups || []).filter(m => !m?.deleted_at);
  if (!mockups.length) return "";
  const last = mockups[mockups.length - 1];
  const ver = (last?.versions || []).find(v => v?.version === last?.current_version);
  return ver?.file_id || (last?.versions || [])[0]?.file_id || "";
}

// Iter 651 — Batas kirim ringkas (WIB) untuk prioritas; tidak mengubah status.
function deadlineInfo(iso) {
  if (!iso) return { text: "Batas kirim belum ada", cls: "none" };
  const d = new Date(iso);
  const h = (d.getTime() - Date.now()) / 36e5;
  const t = d.toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  if (h < 0) return { text: `TERLAMBAT · ${t}`, cls: "late" };
  if (h < 24) return { text: `< 24 jam · ${t}`, cls: "near" };
  return { text: `Kirim ${t}`, cls: "ok" };
}

function JobCard({ job, tab, onStart, onDone, busy }) {
  const [note, setNote] = useState("");
  const [zoomUrl, setZoomUrl] = useState("");
  const [thumbErr, setThumbErr] = useState(false);
  const [more, setMore] = useState(false);
  const thumbId = pickMockupFileId(job);
  const dl = deadlineInfo(job.deadline_iso);
  // Thumbnail ringan: max 300 px (JPEG Q72 di backend, cache 24 jam).
  const thumbUrl = thumbId
    ? `${API}/shopee/files/${thumbId}/thumbnail?size=300`
    : "";
  const fullUrl = thumbId
    ? `${API}/shopee/files/${thumbId}/preview`
    : "";
  const isReady = job.press_state === "ready";
  const inProgress = job.press_state === "in_progress";
  const isWaiting = tab === "menunggu";
  // Iter 549 — HOLD aktif → tombol press disabled, label jelas + alasan.
  const hold = job.hold || {};
  const isHold = !!hold.active;
  return (
    <div className={`shopee-press-card phub-click${more ? " phub-open" : ""}`} onClick={() => setMore(v => !v)} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setMore(v => !v); } }}
      aria-expanded={more} data-accent={isHold || dl.cls === "late" ? "late" : inProgress ? "work" : isReady ? "ready" : isWaiting ? "wait" : job.press_done_at ? "ready" : "info"} data-testid={`press-card-${job.id}`}>
      <ShopeeCancelTag job={job}/>
      {isHold && (
        <div className="sablon-hold-banner" data-testid={`press-hold-banner-${job.id}`}>
          <strong>⛔ PEKERJAAN DIHENTIKAN</strong>
          {hold.status && <span> · {hold.status === "pending" ? "menunggu keputusan" : "dihold Rido"}</span>}
          {hold.reason && <div className="sablon-hold-reason">Alasan: {hold.reason}</div>}
          {hold.requested_by_name && <div className="muted small">Diajukan oleh: {hold.requested_by_name}</div>}
        </div>
      )}
      <div style={{ display: "flex", gap: 10 }}>
        {thumbUrl && !thumbErr ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomUrl(fullUrl); }}
            aria-label="Lihat mockup ukuran besar"
            data-testid={`press-thumb-${job.id}`}
            style={{ padding: 0, border: 0, background: "transparent", cursor: "zoom-in", flex: "0 0 72px" }}
          >
            <img
              src={thumbUrl}
              alt="mockup"
              width={72}
              height={72}
              loading="lazy"
              style={{ borderRadius: 6, objectFit: "cover", display: "block", width: 72, height: 72 }}
              onError={() => setThumbErr(true)}
            />
          </button>
        ) : (
          <div
            data-testid={`press-thumb-missing-${job.id}`}
            style={{
              flex: "0 0 72px", width: 72, height: 72, borderRadius: 6,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "var(--surface-muted, #f1f5f9)", color: "var(--muted, #64748b)",
              fontSize: 10, textAlign: "center", padding: 4, lineHeight: 1.15,
            }}
          >
            {thumbErr ? "Gambar gagal dimuat" : "Mockup belum tersedia"}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>
            <ChannelTag/> {job.order_number} <span className="muted" style={{ fontSize: 11 }}>· {job.customer_account || "-"}</span>
            <TokopediaAttachments orderNumber={job.order_number}/>
          </div>
          <div className="press-card-prod" data-testid={`press-prod-${job.id}`}>
            {job.items_summary || job.design_title || "Produk belum tersinkron"}{job.pcs ? <b> · {job.pcs} pcs</b> : null}
          </div>
          <div className="press-card-meta">
            <span className={`press-dl ${dl.cls}`} data-testid={`press-deadline-${job.id}`}>{dl.text}</span>
            <span className={`press-st${isHold ? " hold" : isWaiting ? " wait" : inProgress ? " work" : isReady ? " ready" : ""}`}>{isHold ? "DITAHAN" : inProgress ? "DIKERJAKAN" : isReady ? "SIAP" : isWaiting ? "MENUNGGU" : job.press_done_at ? "SELESAI" : ""}</span>
            <span className="press-more" aria-hidden="true" data-testid={`press-more-${job.id}`}>{more ? "Tutup" : "Detail"}</span>
          </div>
          {isWaiting && (
            <div className="press-waiting-reason" data-testid={`press-waiting-${job.id}`}>
              {shopeeWaitText(job)}
            </div>
          )}
        </div>
      </div>
      {more && <dl className="phub-detail" onClick={(e) => e.stopPropagation()} data-testid={`press-detail-${job.id}`}>
        <dt>Pesanan</dt><dd>{job.order_number} · {job.customer_account || "-"}</dd>
        <dt>Produk</dt><dd>{job.items_summary || job.design_title || "-"}{job.pcs ? ` · ${job.pcs} pcs` : ""}</dd>
        {job.design_title && job.items_summary ? <><dt>Desain</dt><dd>{job.design_title}</dd></> : null}
        <dt>Proses</dt><dd>{processLabel(job.process) || "-"}</dd>
        <dt>Status</dt><dd>{isHold ? "Dihentikan (HOLD)" : inProgress ? "Sedang dipress" : isReady ? "Siap press" : isWaiting ? "Menunggu divisi sebelumnya" : job.press_done_at ? "Selesai press" : "-"}</dd>
        <dt>Operator Press</dt><dd>{job.press_done_by_name || job.press_started_by_name || "Belum ada"}</dd>
        <dt>Batas kirim</dt><dd>{dl.text}</dd>
        <dt>Divisi sebelumnya</dt><dd>{[job.warehouse_completed_by && `Gudang: ${job.warehouse_completed_by}`, job.sablon_done_by_name && `Sablon/DTF: ${job.sablon_done_by_name}`,
          job.bordir_executed_by_name && `Bordir: ${job.bordir_executed_by_name}`].filter(Boolean).join(" · ") || "Belum tercatat"}</dd>
        {isWaiting && <><dt>Alasan menunggu</dt><dd>{job.press_waiting_reason || "alasan belum tercatat"}</dd></>}
        {(job.press_ready_at || job.press_started_at || job.press_done_at) && <><dt>Waktu</dt><dd>
          {[job.press_ready_at && `siap ${_fmtDate(job.press_ready_at)}`, job.press_started_at && `mulai ${_fmtDate(job.press_started_at)}`,
            job.press_done_at && `selesai ${_fmtDate(job.press_done_at)}`].filter(Boolean).join(" · ")}</dd></>}
        {job.press_done_at && <><dt>Berikutnya</dt><dd>{job.shella_finalized_at ? "Sudah diterima Shella" : "Menuju Shella / pengiriman"}</dd></>}
      </dl>}
      {(isReady || inProgress) && !isHold && (
        <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 8, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Catatan (opsional)"
            data-testid={`press-note-${job.id}`}
            className="input small"
            style={{ flex: 1, minWidth: 120, fontSize: 12 }}
            disabled={busy}
          />
          {isReady && (
            <button
              type="button"
              className="btn primary press-big-btn"
              onClick={() => onStart(job.id, note)}
              disabled={busy}
              data-testid={`press-start-${job.id}`}
            >
              {busy ? "Menyimpan…" : "MULAI PRESS"}
            </button>
          )}
          {inProgress && (
            <button
              type="button"
              className="btn primary press-big-btn"
              onClick={() => onDone(job.id, note)}
              disabled={busy}
              data-testid={`press-done-${job.id}`}
            >
              {busy ? "Menyimpan…" : "SELESAI PRESS"}
            </button>
          )}
        </div>
      )}
      {zoomUrl && (
        <div
          className="sablon-zoom-overlay"
          onClick={(e) => { e.stopPropagation(); setZoomUrl(""); }}
          role="button"
          aria-label="Tutup pratinjau"
          data-testid={`press-zoom-overlay-${job.id}`}
        >
          <img src={zoomUrl} alt="mockup besar" className="sablon-zoom-img" />
        </div>
      )}
    </div>
  );
}

export default function ShopeePressPanel({ operator, tab: ctrlTab, setTab: ctrlSetTab, lateOnly = false, onClearLate, hideTabs = false }) {
  const [tabS, setTabS] = useState("siap_press"); // siap_press | dikerjakan | menunggu | ditahan | riwayat
  const tab = ctrlTab || tabS;
  const setTab = ctrlSetTab || setTabS;
  const [data, setData] = useState({ siap_press: [], dikerjakan: [], menunggu: [], ditahan: [], riwayat: [], counts: {} });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const [showAll, setShowAll] = useState(false);
  const loaded = useRef(false);
  const load = useCallback(async () => {
    if (!loaded.current) setLoading(true);
    try {
      const r = await axios.get(`${API}/shopee/press/queue`, {
        params: {
          include_waiting: true,
          include_history: tab === "riwayat",
        },
        withCredentials: true, timeout: 15_000,
      });
      setData(r?.data || {});
      setErr("");
      loaded.current = true;
      const d = r?.data || {};
      const now = Date.now();
      const late = [...(d.siap_press || []), ...(d.dikerjakan || [])].filter(j => j.deadline_iso && new Date(j.deadline_iso).getTime() < now).length;
      const cc = d.counts || {};
      window.dispatchEvent(new CustomEvent("press-shopee-summary", { detail: { siap: cc.siap_press || 0, dik: cc.dikerjakan || 0, late,
        menunggu: cc.menunggu || 0, ditahan: cc.ditahan || 0, riwayat: cc.riwayat || 0 } }));
    } catch (e) {
      if (e?.response?.status === 403) {
        setData({});
      } else {
        setErr(loaded.current ? "Koneksi bermasalah — menampilkan data terakhir yang berhasil dimuat." : "Gagal memuat pekerjaan. Periksa koneksi, sistem akan mencoba lagi otomatis.");
        window.dispatchEvent(new CustomEvent("press-shopee-summary", { detail: { error: true } }));
      }
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { setShowAll(false); }, [tab]);
  useEffect(() => { load(); }, [load]);
  // Iter 648 — Muat ulang saat sidik antrean Press berubah (PressLiveBar), tanpa mengganti tab.
  useEffect(() => {
    window.addEventListener("press-queue-changed", load);
    return () => window.removeEventListener("press-queue-changed", load);
  }, [load]);

  const act = async (url, jobId, note) => {
    if (busy) return;
    if (!window.confirm(url.includes("start") ? "Mulai press pekerjaan ini sekarang?" : "Pekerjaan press ini sudah selesai?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/shopee/${jobId}${url}`, { note: note || "" }, { withCredentials: true, timeout: 15_000 });
      toast.success("Berhasil. Pekerjaan sudah disimpan.");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error("Gagal menyimpan. Coba lagi.", { description: typeof d === "string" ? d : undefined });
    } finally {
      setBusy(false);
    }
  };

  const isLate = (j) => j.deadline_iso && new Date(j.deadline_iso).getTime() < Date.now();
  const list = (() => {
    if (lateOnly) return [...(data.siap_press || []), ...(data.dikerjakan || [])].filter(isLate);
    if (tab === "siap_press") return data.siap_press || [];
    if (tab === "dikerjakan") return data.dikerjakan || [];
    if (tab === "menunggu") return data.menunggu || [];
    if (tab === "ditahan") return data.ditahan || [];
    return data.riwayat || [];
  })();

  const counts = data.counts || {};
  const tabs = [
    { key: "siap_press", label: "Siap Press", count: counts.siap_press || 0 },
    { key: "dikerjakan", label: "Dikerjakan", count: counts.dikerjakan || 0 },
    { key: "menunggu", label: "Menunggu", count: counts.menunggu || 0 },
    { key: "ditahan", label: "Ditahan", count: counts.ditahan || 0 },
    { key: "riwayat", label: "Riwayat", count: counts.riwayat || 0 },
  ].filter(t => t.key !== "ditahan" || t.count > 0 || tab === "ditahan");

  return (
    <section className="shopee-press-panel" data-testid="shopee-press-panel">
      {lateOnly && <LateBanner count={list.length} onClear={onClearLate} testid="shopee-press-late"/>}
      {!hideTabs && <div role="tablist" aria-label="Tab antrean press" className="shopee-press-tabs">
        {tabs.map(t => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={!lateOnly && tab === t.key}
            onClick={() => { setTab(t.key); if (lateOnly && onClearLate) onClearLate(); }}
            data-testid={`shopee-press-tab-${t.key}`}
            className={`press-tab-btn${!lateOnly && tab === t.key ? " active" : ""}`}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>}
      {err && <div className="warn-box small" role="alert" data-testid="shopee-press-err">{err}</div>}
      {loading && <div className="muted small">Memuat…</div>}
      {!loading && !(err && !loaded.current) && list.length === 0 && (
        <div className="shopee-press-empty muted small" data-testid="shopee-press-empty">
          {tab === "siap_press" ? (counts.menunggu
            ? <>Belum ada yang Siap Press. <b>{counts.menunggu} pekerjaan masih Menunggu</b> divisi sebelumnya.{" "}
              <button type="button" className="press-goto-wait" onClick={() => setTab("menunggu")} data-testid="shopee-press-goto-waiting">Lihat Menunggu ({counts.menunggu})</button></>
            : "Belum ada pekerjaan siap press.") :
           tab === "dikerjakan" ? "Belum ada press yang sedang dikerjakan." :
           tab === "menunggu" ? "Semua pekerjaan Shopee sudah lanjut atau belum ada pekerjaan sablon aktif." :
           tab === "ditahan" ? "Tidak ada pekerjaan Press yang sedang dihentikan." :
           "Belum ada riwayat press."}
        </div>
      )}
      {!loading && list.length > 0 && (
        <div className="shopee-press-list" data-testid="shopee-press-list">
          {(showAll ? list : list.slice(0, 5)).map(j => (
            <JobCard
              key={j.id}
              job={j}
              tab={tab}
              busy={busy}
              onStart={(id, n) => act("/press/start", id, n)}
              onDone={(id, n) => act("/press/done", id, n)}
            />
          ))}
          {list.length > 5 && <button type="button" className="press-showall" onClick={() => setShowAll(v => !v)} data-testid="shopee-press-showall">
            {showAll ? "Tampilkan lebih sedikit" : `Tampilkan semua (${list.length})`}</button>}
        </div>
      )}
    </section>
  );
}
